import { NavigableTreeNode, NodeId, TreeNode } from '../types';

// ===========================================================================
// Node transformation and updates
// ===========================================================================

/**
 * Transform a node to a navigable node.
 * @param node The node to transform.
 * @param currentChapterId The currently active node id.
 * @param level The level of the node.
 * @param posInSet The position of the node in the set.
 * @param setSize The size of the set.
 * @returns The transformed node.
 */
export function transformNodeToNavigable(
  node: TreeNode,
  currentChapterId?: number,
  level = 1,
  posInSet = 1,
  setSize = 1,
): NavigableTreeNode {
  return {
    ...node,
    isExpanded: false,
    children:
      node.children?.map((child, index) =>
        transformNodeToNavigable(
          child,
          currentChapterId,
          level + 1,
          index + 1,
          node.children?.length ?? 1,
        ),
      ) ?? null,
    level,
    posInSet,
    setSize,
  };
}

/**
 * Apply updates to a given node in the node tree.
 * @param nodes The nodes to look through.
 * @param nodeId The node id to update.
 * @param updates The updates to apply to the node.
 */
export function updateNodeRecursive(
  nodes: NavigableTreeNode[],
  nodeId: NodeId,
  updates: Partial<NavigableTreeNode>,
): NavigableTreeNode[] {
  return nodes.map((node) => {
    if (node.nodeId === nodeId) {
      return { ...node, ...updates };
    }
    if (node.children) {
      return {
        ...node,
        children: updateNodeRecursive(node.children, nodeId, updates),
      };
    }
    return node;
  });
}
